import { useState, useEffect, useMemo, useRef } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { format } from 'date-fns';
import styled from 'styled-components';
import { MozartModal, MozartButton } from '@pgforsta/mozart_ui/lib/v4';
import { MozartCheckBox, MozartLoading } from '@pgforsta/mozart_ui';
import ModalWrapper from '../styled/ModalWrapper';
import CheckboxCell from '../styled/CheckboxCell';
import Table from '../styled/Table.js';
import {
  getSourceReviews,
  deleteSourceReviews,
} from '../../api/services/sources';
import useAsync from '../../hooks/async';
import useCheckboxes from '../../hooks/checkboxes';

const Status = styled.p`
  margin: 20px 0;
  text-align: center;
`;

const Disclaimer = styled.div`
  margin-top: 5px;
  font-size: 0.8em;
`;

const DateCell = styled.div`
  font-weight: 600;
`;

const LIMIT = 10;
const DATE_FORMAT = 'MM/dd/y';

const ReviewsModal = ({ source, onClose }) => {
  const query = useAsync();
  const deleteQuery = useAsync();
  const [reviews, setReviews] = useState([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [hasDeletedRecords, setHasDeletedRecords] = useState(false);
  const checkboxes = useCheckboxes(reviews);
  const modalWrapperRef = useRef(null);

  useEffect(() => {
    if (!Boolean(source?.sourceId)) {
      setReviews([]);
      setTotalReviews(0);
      checkboxes.resetCheckedItems();
      return;
    }
    fetchRecords(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const checkedDocumentIds = useMemo(
    () => checkboxes.checkedItems.map(({ documentId }) => documentId),
    [checkboxes.checkedItems]
  );

  const fetchRecords = (reset = false) => {
    query.run(
      getSourceReviews(source.sourceId, source.harvesterId, {
        pageNo: reset ? 0 : Math.floor(reviews.length / LIMIT),
        pageSize: LIMIT,
      }).then((data) => {
        const { content, totalElements } = data;
        setReviews((prevReviews) =>
          reset ? content : prevReviews.concat(content)
        );
        setTotalReviews(totalElements);
        return data;
      })
    );
  };

  const handleAllReviewsCheck = (event) => {
    checkboxes.toggleAll(event.target.checked);
  };

  const handleReviewCheck = (checked, review) => {
    checkboxes.toggleItem(
      checked,
      review,
      ({ documentId }) => documentId !== review.documentId
    );
  };

  const handleDeleteClick = () => {
    deleteQuery.run(deleteSourceReviews(checkedDocumentIds)).then(() => {
      setHasDeletedRecords(true);
      fetchRecords(true);
    });
  };

  const getHeaders = () => [
    <CheckboxCell>
      <MozartCheckBox>
        <input
          id="reviews-select-all"
          name="reviews-select-all"
          type="checkbox"
          checked={checkboxes.allChecked}
          onChange={handleAllReviewsCheck}
        />
        <label htmlFor="reviews-select-all" />
      </MozartCheckBox>
    </CheckboxCell>,
    'Date',
    'Review',
  ];

  const getRows = () =>
    reviews.map((review) => {
      const { documentId } = review;
      const checkboxId = `reviews-select-${documentId}`;
      return [
        <CheckboxCell>
          <MozartCheckBox>
            <input
              type="checkbox"
              id={checkboxId}
              name={checkboxId}
              checked={checkedDocumentIds.includes(documentId)}
              onChange={(event) =>
                handleReviewCheck(event.target.checked, review)
              }
            />
            <label htmlFor={checkboxId} />
          </MozartCheckBox>
        </CheckboxCell>,
        <DateCell>
          {format(new Date(review.mentionTime), DATE_FORMAT)}
        </DateCell>,
        review.content,
      ];
    });

  /**
   * After spending a lot of time dealing with the infinite scroll not
   * triggerring the `loadMore` prop function, it was discovered that it was
   * caused by having it somewhere down the DOM tree a parent would have an
   * overflow style set. Setting the `useWindow` to false would not be enough,
   * so the `getScrollParent` had to be used returning the DOM element where
   * the `overflow` property is defined. Yet, having such property with a value
   * of scroll wouldn't make it work, it needs to be auto.
   */
  const getScrollParent = () => {
    const { current: node } = modalWrapperRef;
    if (!node) {
      return null;
    }
    /**
     * "Dirty" way to get the first scrollable parent.
     * Root element (ref) > wrapper > modal > contents > body
     */
    return node.firstChild.firstChild.lastChild.children[1];
  };

  const renderModalTitle = () => {
    return (
      <div>
        <div>{`Reviews - ${source?.sourceName}`}</div>
        <Disclaimer>*Engagements and ratings are excluded</Disclaimer>
      </div>
    );
  };

  const renderModalFooter = () => {
    if (reviews.length === 0) {
      return null;
    }
    return (
      <MozartButton
        outline
        disabled={checkboxes.noneChecked || deleteQuery.isPending}
        onClick={handleDeleteClick}
      >
        Delete Selected
      </MozartButton>
    );
  };

  return (
    <div id="reviews-modal" ref={modalWrapperRef}>
      <ModalWrapper
        contentPadding="0"
        footerPadding="20px 20px 20px 65px"
        scrollableContent
      >
        <MozartModal
          show={Boolean(source?.sourceId)}
          onClose={() => onClose(hasDeletedRecords)}
          title={renderModalTitle()}
          size="large"
          footer={renderModalFooter()}
        >
          <InfiniteScroll
            initialLoad={false}
            useWindow={false}
            loadMore={() => fetchRecords()}
            hasMore={query.isResolved && reviews.length < totalReviews}
            getScrollParent={getScrollParent}
          >
            <Table
              id="source-reviews-table"
              headers={getHeaders()}
              rows={getRows()}
              alternateRows={false}
              stickyHeaders
            />
          </InfiniteScroll>

          {query.isPending && <MozartLoading inline />}

          {query.isRejected && (
            <Status>There was an error loading the reviews.</Status>
          )}
        </MozartModal>
      </ModalWrapper>
    </div>
  );
};

export default ReviewsModal;
